import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useStaggerReveal } from '@/hooks/useStaggerReveal';

const services = [
  {
    icon: 'ri-code-box-line',
    title: 'Software Development',
    description:
      'Custom web and mobile applications, internal tools and full business systems — designed around your workflow and built to run in production.',
    image:
      'https://readdy.ai/api/search-image?query=Modern%20software%20development%20workspace%20with%20multiple%20monitors%20showing%20code%20and%20dashboards%2C%20dark%20theme%20interface%2C%20subtle%20aurora%20green%20accent%20highlights%2C%20professional%20tech%20environment%2C%20cinematic%20dark%20lighting%2C%20clean%20minimal%20composition%2C%20no%20text&width=800&height=600&seq=service-software-dev-v2&orientation=landscape',
    stat: 'Full Stack',
    accent: 'primary',
  },
  {
    icon: 'ri-robot-2-line',
    title: 'AI Development',
    description:
      'Chatbots and assistants, automation for repetitive work, predictive models and analysis that turns your data into decisions — built into the tools you use.',
    image:
      'https://readdy.ai/api/search-image?query=Abstract%20artificial%20intelligence%20visualization%20with%20glowing%20neural%20network%20patterns%2C%20flowing%20data%20nodes%20and%20connections%2C%20aurora%20green%20and%20warm%20amber%20glowing%20lines%2C%20minimal%20dark%20background%2C%20futuristic%20tech%20aesthetic%2C%20cinematic%20lighting%2C%20no%20text&width=800&height=600&seq=service-ai-dev-v2&orientation=landscape',
    stat: 'Intelligence-Led',
    accent: 'accent',
  },
  {
    icon: 'ri-megaphone-line',
    title: 'Marketing Tools',
    description:
      'The software behind digital marketing — targeting, automation and campaign measurement — running on the same stack as everything else.',
    image:
      'https://readdy.ai/api/search-image?query=Digital%20marketing%20analytics%20dashboard%20with%20charts%20and%20data%20visualization%2C%20dark%20theme%20interface%2C%20aurora%20yellow%20and%20green%20accent%20highlights%2C%20professional%20business%20intelligence%20aesthetic%2C%20cinematic%20dark%20lighting%2C%20clean%20composition%2C%20no%20text&width=800&height=600&seq=service-marketing-v2&orientation=landscape',
    stat: 'Performance-Driven',
    accent: 'primary',
  },
];

export default function WhatWeBuildSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: cardsRef, isVisible: cardsVisible } = useStaggerReveal({ threshold: 0.1 });

  return (
    <section id="what-we-build" className="relative py-24 md:py-36 bg-background-900">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div
          ref={headerRef}
          className={`mb-16 md:mb-24 transition-all duration-1000 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <h2 className="mt-6 font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight max-w-4xl">
            Connected technology
            <br />
            <span className="text-primary-400">Seamless execution</span>
          </h2>
        </div>

        {/* Cards */}
        <div
          ref={cardsRef}
          className={`grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 stagger-reveal ${cardsVisible ? 'visible' : ''}`}
        >
          {services.map((service, index) => (
            <div
              key={index}
              className="group relative bg-background-950 rounded-2xl overflow-hidden card-lift cursor-pointer"
            >
              {/* Image area */}
              <div className="relative h-56 md:h-64 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                  width={800}
                  height={600}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/50 to-transparent" />
                {/* Arrow indicator */}
                <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                  <i className="ri-arrow-right-up-line text-white" />
                </div>
              </div>

              {/* Content area */}
              <div className="p-6 md:p-8">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 bg-primary-500/15">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${service.icon} text-lg text-primary-400`} />
                  </span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-white/40 text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>

              {/* Bottom accent line */}
              <div className="absolute bottom-0 left-0 w-full h-1 bg-primary-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}